import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { useEffect, useRef, useState } from 'react'
import { _x } from '@wordpress/i18n'
import Pin from './Pin'
import { type MarkerItem, getMarkerColor, getMarkerIcon } from './types'
import { getLocationUrl, type LocationUrlConfig } from './urlUtils'
import './MarkerWithPopup.scss'

export type MarkerWithPopupProps = {
  item: MarkerItem
  isSelected: boolean
  onSelect: () => void
  onClose: () => void
  urlConfig: LocationUrlConfig
}

export default function MarkerWithPopup({
  item,
  isSelected,
  onSelect,
  onClose,
  urlConfig
}: MarkerWithPopupProps) {
  const color = getMarkerColor(item.status)
  const icon = getMarkerIcon(item.status)
  const [isClosing, setIsClosing] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const prevSelected = useRef(isSelected)
  const url = getLocationUrl(item, urlConfig)

  useEffect(() => {
    if (isSelected && !prevSelected.current) {
      setShowPopup(true)
    } else if (!isSelected && prevSelected.current && !isClosing) {
      setIsClosing(true)
      setTimeout(() => {
        setIsClosing(false)
        setShowPopup(false)
      }, 200)
    }
    prevSelected.current = isSelected
  }, [isSelected])

  useEffect(() => {
    if (!showPopup) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        ;(document.activeElement as HTMLElement)?.blur()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showPopup, onClose])

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  return (
    <AdvancedMarker
      position={{
        lat: item._geoloc?.lat ?? 0,
        lng: item._geoloc?.lng ?? 0
      }}
      zIndex={showPopup ? 100 : (item.status === 'icf' ? 1 : 0)}
      clickable={true}
      onClick={() => {
        if (!showPopup) onSelect()
      }}
    >
      <div
        className={`location-marker ${showPopup ? 'location-marker--expanded' : ''}`}
      >
        {showPopup ? (
          <div
            className={`location-marker__popup location-marker__popup--${color} ${isClosing ? 'location-marker__popup--closing' : ''}`}
          >
            <button
              className="location-marker__close"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
            <div className="location-marker__logo">
              {icon ? (
                <i className={icon} />
              ) : (
                <svg viewBox="0 0 3105 1534">
                  <path
                    d="M1202.19 135.113C333.658 422.363 213.621 1260.71 331.925 1397.36 445.138 1528.13 1822.8 517.317 1346.27 473.163c-262.57-24.334-635.691 99.579-721.078 172.141C1161.65-192.525 3252.62-191.15 3092.64 517.154c-86.49 382.95-1077.02 809.246-1623.46 925.346-2083.067 442.56-1745.401-851.837-266.99-1307.387Zm1112.96 355.041 52.35-143.825c15.11-41.521 13.85-74.354-3.78-98.529-20.79-27.925-62.52-41.887-125.18-41.887-72.47 0-130.41 13.962-173.79 41.887-37.48 24.175-65.42 61.533-83.83 112.129l-185.89 510.738c-16.77 46.046-10.2 81.921 19.7 107.575 27.74 23.418 71.05 35.098 129.93 35.098 55.11 0 105.11-13.944 150.02-41.886 38.98-24.158 64.12-51.712 75.38-82.666l73.37-201.596h-158.55l-71.3 195.929c-2.2 6.05-5.88 10.962-11.02 14.725-4.88 3.029-11.45 4.521-19.77 4.521-6.79 0-12.29-1.492-16.48-4.521-3.16-3.763-3.64-8.675-1.43-14.725l200.31-550.375c2.49-6.804 6.16-11.717 11.03-14.721 5.63-3.021 12.59-4.525 20.9-4.525 8.31 0 13.8 1.504 16.48 4.525 2.68 3.004 2.77 7.917.3 14.721l-57.3 157.408h158.55Zm24.64 514.136 138.49-380.511h135.9l35.03-96.25h-135.9l77.9-214.029h151.76l37.41-107.65h-125.02c-66.92 0-119.56 12.65-171.84 41.817-39.98 27.987-66.92 65.254-83.53 112.071L2165.4 1004.29h174.39Zm-638.89-4.28 288.52-792.727h-174.39L1526.5 1000.01h174.4Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </div>
            <h4 className="location-marker__name">{item.name}</h4>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`location-marker__button btn btn-sm btn-outline-default`}
                onClick={(e) => e.stopPropagation()}
              >
                {_x('Visit Website', 'location marker button', 'icf')}
              </a>
            )}
          </div>
        ) : (
          <Pin title={item.name} color={color} icon={icon} />
        )}
      </div>
    </AdvancedMarker>
  )
}
